import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Paper, Breadcrumbs, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useNotification } from '../../../components/Notification';
import { Add as AddIcon, Delete as DeleteIcon, Category as CategoryIcon, NavigateNext as NavigateNextIcon, VerifiedUser as VerifyIcon } from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddVerifyModeModal from './AddVerifyModeModal';
import { hasPermission } from '../../../utils/permissions';
import {
    fetchVerifyModes,
    fetchVerifyModeById,
    addVerifyMode,
    updateVerifyMode,
    selectVerifyModes,
    selectVerifyModesLoading,
    selectVerifyModesError,
    selectVerifyModesSuccessMessage,
    selectVerifyModesTotal,
    selectVerifyModesPage,
    selectVerifyModesPageSize,
    selectVerifyModesSortColumn,
    selectVerifyModesSortDirection,
    selectVerifyModesFilterName,
    selectSelectedVerifyMode,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    deleteVerifyMode,
    bulkDeleteVerifyModes,
    clearError,
    clearSuccessMessage,
} from './slice';

function VerifyModes() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const verifyModes = useSelector(selectVerifyModes);
    const loading = useSelector(selectVerifyModesLoading);
    const error = useSelector(selectVerifyModesError);
    const successMessage = useSelector(selectVerifyModesSuccessMessage);
    const total = useSelector(selectVerifyModesTotal);
    const page = useSelector(selectVerifyModesPage);
    const pageSize = useSelector(selectVerifyModesPageSize);
    const sortColumn = useSelector(selectVerifyModesSortColumn);
    const sortDirection = useSelector(selectVerifyModesSortDirection);
    const filterName = useSelector(selectVerifyModesFilterName);
    const selectedVerifyMode = useSelector(selectSelectedVerifyMode);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [verifyModeToDelete, setVerifyModeToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // Check permissions
    const canAddVerifyMode = hasPermission('verifymode', 'addVerifyMode');
    const canEditVerifyMode = hasPermission('verifymode', 'editVerifyMode');
    const canDeleteVerifyMode = hasPermission('verifymode', 'deleteVerifyMode');

    // Fetch verify modes
    useEffect(() => {
        dispatch(fetchVerifyModes({ page, pageSize, sortColumn, sortDirection, filterName }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch verify modes to update the list
            dispatch(fetchVerifyModes({ page, pageSize, sortColumn, sortDirection, filterName }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

    // Helper function to escape HTML and prevent XSS
    const escapeHtml = (text) => {
        if (!text) return '-';
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    // Define table columns
    const columns = [
        {
            key: 'name',
            label: t('verifyModes.table.name'),
        },
        {
            key: 'description',
            label: t('verifyModes.table.description'),
            render: (value) => escapeHtml(value),
        },
        {
            key: 'status',
            label: t('verifyModes.table.status'),
            render: (value) => {
                const status = value === 1 || value === '1' || value === true;
                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: status ? '#d4edda' : '#f8d7da',
                            color: status ? '#155724' : '#721c24',
                        }}
                    >
                        {status ? t('verifyModes.status.active') : t('verifyModes.status.inactive')}
                    </Box>
                );
            },
        },
        {
            key: 'isDefault',
            label: t('verifyModes.table.isDefault'),
            render: (value) => {
                const isDefault = value === true || value === 1 || value === '1';
                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: isDefault ? '#d4edda' : '#e2e8f0',
                            color: isDefault ? '#155724' : '#64748b',
                        }}
                    >
                        {isDefault ? t('verifyModes.isDefault.yes') : t('verifyModes.isDefault.no')}
                    </Box>
                );
            },
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch verify mode details by ID
        dispatch(fetchVerifyModeById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setVerifyModeToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (verifyModeToDelete?.id) {
            dispatch(deleteVerifyMode(verifyModeToDelete.id));
        }
        setShowDeleteDialog(false);
        setVerifyModeToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setVerifyModeToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteVerifyModes(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle filter name change
    const handleFilterNameChange = (event) => {
        dispatch(setFilterName(event.target.value));
    };

    // Handle add verify mode - show dialog
    const handleAddVerifyMode = () => {
        setShowAddDialog(true);
    };

    // Handle close add verify mode dialog
    const handleCloseAddVerifyMode = () => {
        setShowAddDialog(false);
    };

    // Confirm add verify mode
    const handleConfirmAddVerifyMode = (verifyModeData) => {
        dispatch(addVerifyMode(verifyModeData));
        setShowAddDialog(false);
    };

    // Handle close edit verify mode dialog
    const handleCloseEditVerifyMode = () => {
        setShowEditDialog(false);
    };

    // Confirm edit verify mode
    const handleConfirmEditVerifyMode = (verifyModeData) => {
        dispatch(updateVerifyMode(verifyModeData));
        setShowEditDialog(false);
    };

    // Check if row is deletable (not default)
    const isRowDeletable = (row) => {
        return !row.isDefault;
    };

    // Check if row is selectable (not default)
    const isRowSelectable = (row) => {
        return !row.isDefault;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CategoryIcon fontSize="small" />
                        <Typography variant="body2">{t('verifyModes.breadcrumb.catalog')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('verifyModes.breadcrumb.verifyModes')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('verifyModes.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddVerifyMode && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddVerifyMode}
                                id="addVerifyMode"
                                name="addVerifyMode"
                                data-testid="verifyModes-button-addVerifyMode"
                            >
                                {t('verifyModes.addVerifyMode')}
                            </Button>
                        )}
                        {canDeleteVerifyMode && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0}
                                id="deleteSelected"
                                name="deleteSelected"
                                data-testid="verifyModes-button-deleteSelected"
                            >
                                {t('verifyModes.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Input
                            label={t('verifyModes.filterByName')}
                            value={filterName}
                            onChange={handleFilterNameChange}
                            placeholder={t('verifyModes.filterByNamePlaceholder')}
                            id="filterName"
                            name="filterName"
                            data-testid="verifyModes-input-filterName"
                            sx={{ width: 320 }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="verifyModes-table"
                        columns={columns}
                        data={verifyModes}
                        selectable={true}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        isRowSelectable={isRowSelectable}
                        onEdit={canEditVerifyMode ? handleEdit : null}
                        onDelete={canDeleteVerifyMode ? handleDelete : null}
                        isRowDeletable={isRowDeletable}
                        loading={loading}
                        emptyMessage={t('verifyModes.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={['name']}
                    />
                </Box>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('verifyModes.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('verifyModes.deleteDialog.message', {
                            name: verifyModeToDelete?.name || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="verifyModes-button-cancelDelete"
                    >
                        {t('verifyModes.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="verifyModes-button-confirmDelete"
                    >
                        {t('verifyModes.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('verifyModes.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('verifyModes.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="verifyModes-button-cancelBulkDelete"
                    >
                        {t('verifyModes.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="verifyModes-button-confirmBulkDelete"
                    >
                        {t('verifyModes.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Verify Mode Modal */}
            <AddVerifyModeModal
                open={showAddDialog}
                onClose={handleCloseAddVerifyMode}
                onConfirm={handleConfirmAddVerifyMode}
            />

            {/* Edit Verify Mode Modal */}
            <AddVerifyModeModal
                open={showEditDialog}
                onClose={handleCloseEditVerifyMode}
                onConfirm={handleConfirmEditVerifyMode}
                verifyModeData={selectedVerifyMode}
                editMode={true}
            />
        </Box>
    );
}

export default VerifyModes;
